import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject, throwError, of } from 'rxjs';
import { catchError, map, pluck, tap, debounceTime, switchMap, shareReplay } from 'rxjs/operators';
import { APP_CONFIG } from 'src/app/core/services/config.service';
import { TooMuchLoadsComponent } from 'src/app/modal/components/too-much-loads/too-much-loads.component';
import { ModalService } from 'src/app/modal/modal.service';
import { LoadFactory } from 'src/app/working-table/visualisation/loadFactory';
import { Project, VersionInfo } from '../objects/project';
import {
  GroupAdd,
  GroupEdit,
  GroupLayerAdd,
  GroupLayerCalculate,
  GroupPalletFill,
  PalletLayerEdit,
  DeletePalletLayerInterlayer,
  PalletLayerGroupEdit,
  OptionsUpdate,
  ProjectInfo,
  SharedProject,
} from '../types';
import { Load } from './../objects/load';
import { ProjectHistoryService } from './project-history.service';
import { ProjectService } from './project.service';
import { SharedProjectService } from './shared-project.service';
import { UserApiService } from './user-api.service';
import { rotationData } from '../../working-table/components/load-context-menu/load-context-menu.component';
import { NotificationsService } from './notifications.service';
import { PalletOverflowRecommendationService } from './pallet-overflow-recommendation.service';
import { normalizeCargoSpacePalletLayerGroups } from '../../shared/helpers/pallet-loading-space.helper';
import {
  palletQuantityDifferenceToRestoreOnVehicle,
  palletSpaceQuantity,
  restorePalletWorkspaceQuantity,
} from '../../shared/helpers/pallet-workspace-collapse.helper';

@Injectable({
  providedIn: 'root',
})
export class ProjectApiService {
  private url = 'http://localhost:8080/api/';
  private currentWorkSpace: number;
  public currentProjectName = '';
  public currentProjectNote = '';
  public currentProjectId = '';
  private getProjectVersionsSubject = new Subject<void>();
  private getProjectVersionsObservable: Observable<VersionInfo[]>;
  private getProjectInfoSubject = new Subject<void>();
  private getProjectInfoObservable: Observable<ProjectInfo>;

  constructor(
    private http: HttpClient,
    private projectService: ProjectService,
    private userApiService: UserApiService,
    private sharedProjectService: SharedProjectService,
    private projectHistory: ProjectHistoryService,
    private modalService: ModalService,
    private translate: TranslateService,
    private router: Router,
    private route: ActivatedRoute,
    private notificationService: NotificationsService,
    private palletOverflowRecommendation: PalletOverflowRecommendationService,
  ) {
    APP_CONFIG()
      .pipe(pluck('baseUri'))
      .subscribe((url: string) => (this.url = url));
    this.projectService.currentWorkSpaceIndex.subscribe(
      (currentWorkSpace) => (this.currentWorkSpace = currentWorkSpace),
    );
    this.getProjectVersionsInit();
    this.getProjectInfoInit();
  }

  public getProjectInfo(): Observable<ProjectInfo> {
    this.getProjectInfoSubject.next();
    return this.getProjectInfoObservable;
  }

  private getProjectInfoInit(): void {
    this.getProjectInfoObservable = this.getProjectInfoSubject.pipe(
      debounceTime(300),
      switchMap(() => this.http.get<ProjectInfo>(this.url + 'project-info')),
      map((projectData: ProjectInfo) => this.handleSuccessProjectInfo(projectData)),
      catchError(() => this.handleErrorWithoutValid()),
      shareReplay(1),
    );
    this.getProjectInfoObservable.subscribe();
  }

  private getProjectVersionsInit(): void {
    this.getProjectVersionsObservable = this.getProjectVersionsSubject.pipe(
      debounceTime(500),
      switchMap(() => this.http.get<VersionInfo[]>(this.url + 'project-info/version')),
      tap((projectData: VersionInfo[]) => this.handleSuccessProjectVersions(projectData)),
      catchError(() => this.handleErrorWithoutValid()),
      shareReplay(1),
    );
    this.getProjectVersionsObservable.subscribe();
  }

  public getProject() {
    return this.http.get(this.url + 'project').pipe(
      map((projectData: Project) => this.handleSuccessWithClearLoads(projectData)),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public getProjectById(id: number, version: number): Observable<any> {
    const versionString = version !== null && version !== undefined ? `&version=${version}` : '';
    return this.http
      .get(this.url + `user/project/data?id=${id}${versionString}`)
      .pipe(catchError((error: HttpErrorResponse) => this.handleError(error)));
  }

  public getEmapaLink(): Observable<any> {
    return this.http
      .post(this.url + 'project/shareExternal', {
        clientId: '1b058268-48bb-4e53-81f3-9c94009bffee',
        //     local client clientId: 'c6a882c8-a480-4fb1-8c1c-591d6d51e529',
      })
      .pipe(catchError((error: HttpErrorResponse) => this.handleError(error)));
  }

  public getProjectVersions(): Observable<VersionInfo[]> {
    this.getProjectVersionsSubject.next();
    return this.getProjectVersionsObservable;
  }

  private handleSuccessProjectVersions(versionInfo: VersionInfo[]): VersionInfo[] {
    return versionInfo;
  }

  public newProject(): Observable<Project> {
    this.projectService.setCurrentWorkSpace(0);
    this.projectService.setClosedLoadingSpaces([]);
    return this.http.get(this.url + 'project/new').pipe(
      tap((projectData: Project) => {
        this.palletOverflowRecommendation.onProjectReplaced();
        this.handleSuccessWithClearLoads(projectData);
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public replaceProject(project: Project): void {
    const isConverted: boolean = project.isConverted;
    const convertedProject: Project = project;
    if (isConverted) {
      convertedProject.isConverted = false;
      convertedProject.options.unit = 'cm';
    }
    if (convertedProject.workSpaces.length < this.currentWorkSpace + 1) {
      if (convertedProject.workSpaces.length > 0) {
        this.projectService.setCurrentWorkSpace(convertedProject.workSpaces.length - 1);
      } else if (convertedProject.workSpaces.length === 0) {
        this.projectService.setCurrentWorkSpace(0);
      }
    }
    this.http
      .post(this.url + 'project/replace', { project: convertedProject })
      .pipe(catchError((error: HttpErrorResponse) => this.handleError(error)))
      .subscribe((projectData: Project) => this.handleReplaceSuccess(projectData));
  }

  public addGroup(groupsToAdd: GroupAdd) {
    const quantity = this.projectService.getProjectQuantity();
    if (quantity + Number(groupsToAdd.newLoads[0].quantity) > 10000) {
      return this.modalService.open(TooMuchLoadsComponent, { message: 'too_much_loads_add' }).pipe(
        tap((result: boolean) => {
          if (result) {
            return this.instantAddGroup(groupsToAdd).subscribe();
          }
        }),
      );
    } else {
      return this.instantAddGroup(groupsToAdd);
    }
  }

  public instantAddGroup(groupsToAdd): Observable<any> {
    return this.http.post(this.url + 'project/group/add', groupsToAdd).pipe(
      tap((projectData: Project) => this.handleAddLoadSuccess(projectData)),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  /** Preview layer capacity only — does not update global project (list / 3D) until add. */
  public calculateLayer(body: GroupLayerCalculate): Observable<Project> {
    return this.http.post(this.url + 'project/group/layer/calculate', body).pipe(
      map((projectData: Project | { project: Project }) => {
        const project = this.extractProjectFromResponse(projectData);
        this.normalizeProjectPalletLayerGroups(project);
        return project;
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  /** Fills the active pallet and updates the project with backend-calculated placement. */
  public fillPallet(body: GroupPalletFill): Observable<Project> {
    return this.http.post(this.url + 'project/group/pallet/fill', body).pipe(
      map((projectData: Project | { project: Project }) => {
        const project = this.extractProjectFromResponse(projectData);
        this.normalizeProjectPalletLayerGroups(project);
        return project;
      }),
      tap((projectData: Project) => this.handleAddLoadSuccess(projectData)),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public addPalletLayer(body: GroupLayerAdd): Observable<Project> {
    return this.http.post(this.url + 'project/group/layer/add', body).pipe(
      map((projectData: Project | { project: Project }) => {
        const project = this.extractProjectFromResponse(projectData);
        this.normalizeProjectPalletLayerGroups(project);
        return project;
      }),
      tap((projectData: Project) => this.handleAddLoadSuccess(projectData)),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public editPalletLayerGroup(body: PalletLayerGroupEdit): Observable<Project> {
    const addsLayers = (body.layers ?? []).some((layer) => layer.quantityDifference > 0);
    return this.http.post(this.url + 'project/group/layerGroup/edit', body).pipe(
      map((projectData: Project | { project: Project }) => {
        const project = this.extractProjectFromResponse(projectData);
        this.normalizeProjectPalletLayerGroups(project);
        return project;
      }),
      tap((projectData: Project) =>
        addsLayers
          ? this.handleAddLoadSuccess(projectData)
          : this.handleSuccessWithClearLoads(projectData),
      ),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public deletePalletLayerInterlayer(body: DeletePalletLayerInterlayer): Observable<Project> {
    return this.http.post(this.url + 'project/group/layerGroup/interlayer/delete', body).pipe(
      map((projectData: Project | { project: Project }) => {
        const project = this.extractProjectFromResponse(projectData);
        this.normalizeProjectPalletLayerGroups(project);
        return project;
      }),
      tap((projectData: Project) => this.handleSuccessWithClearLoads(projectData)),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  /** @deprecated Use editPalletLayerGroup */
  public editPalletLayer(body: PalletLayerEdit): Observable<Project | null> {
    if (!body.target.layerId) {
      return this.editPalletLayerGroup({
        currentWorkSpaceId: body.currentWorkSpaceId,
        target: {
          workSpaceId: body.target.workSpaceId,
          stopId: body.target.stopId,
        },
        layerGroupId: body.target.palletLayerGroupId,
      }).pipe(map((p) => p ?? null));
    }
    return this.editPalletLayerGroup({
      currentWorkSpaceId: body.currentWorkSpaceId,
      target: {
        workSpaceId: body.target.workSpaceId,
        stopId: body.target.stopId,
      },
      layerGroupId: body.target.palletLayerGroupId,
      layers: [{ layerId: body.target.layerId, quantityDifference: body.quantityDifference }],
    }).pipe(map((p) => p ?? null));
  }

  public addFromOutside(worSpaceId: number, stopId: number, groupId: number): Observable<any> {
    return this.http
      .post(this.url + 'project/group/addfromoutside', {
        target: {
          workSpaceId: worSpaceId,
          stopId: stopId,
          groupId: groupId,
        },
      })
      .pipe(
        tap((projectData: Project) => this.handleSuccessWithClearLoads(projectData)),
        catchError((error: HttpErrorResponse) => this.handleError(error)),
      );
  }

  public editGroup(groupToEdit: GroupEdit) {
    const quantity = this.projectService.getProjectQuantity();
    if (
      groupToEdit.quantityDifference !== 0 &&
      quantity + Number(groupToEdit.quantityDifference) > 10000
    ) {
      return this.modalService.open(TooMuchLoadsComponent, { message: 'too_much_loads_add' }).pipe(
        tap((result: boolean) => {
          if (result) {
            return this.instantEditGroup(groupToEdit).subscribe();
          }
        }),
      );
    } else {
      return this.instantEditGroup(groupToEdit);
    }
  }

  public instantEditGroup(groupToEdit): Observable<any> {
    return this.http.post(this.url + 'project/group/edit', groupToEdit).pipe(
      tap((projectData: Project) =>
        groupToEdit?.quantityDifference > 0
          ? this.handleAddLoadSuccess(projectData)
          : this.handleSuccessWithClearLoads(projectData),
      ),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public flipGroup(groupToFlip): Observable<any> {
    return this.http.post(this.url + 'project/group/flip', groupToFlip).pipe(
      tap((projectData: Project) => this.handleSuccessWithClearLoads(projectData)),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public deleteGroup(groupToDelete): Observable<any> {
    return this.http.post(this.url + 'project/group/delete', groupToDelete).pipe(
      tap((projectData: Project) => this.handleSuccessWithClearLoads(projectData)),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public deleteOutside(workspaceId: number, stopId: number): Observable<any> {
    return this.http
      .post(this.url + 'project/group/deleteoutside', {
        target: {
          workSpaceId: workspaceId,
          stopId: stopId,
        },
      })
      .pipe(
        tap((projectData: Project) => this.handleSuccessWithClearLoads(projectData)),
        catchError((error: HttpErrorResponse) => this.handleError(error)),
      );
  }

  public deleteAllGroups() {
    this.http
      .post(this.url + 'project/group/deleteall', {})
      .pipe(catchError((error: HttpErrorResponse) => this.handleError(error)))
      .subscribe((projectData: Project) => this.handleSuccessWithClearLoads(projectData));
  }

  public projectOptionsUpdate(optionsUpdate: OptionsUpdate): Observable<any> {
    return this.http.post(this.url + 'project/options/update', optionsUpdate).pipe(
      tap((projectData: Project) => {
        if (optionsUpdate.options.unit) {
          return this.handleSetUnitSuccess(projectData);
        } else {
          return this.handleSuccessWithClearLoads(projectData);
        }
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public setGroupsOrder(groupsOrder) {
    return this.http.post(this.url + 'project/group/drag', groupsOrder).pipe(
      tap((projectData: Project) => this.handleSuccessWithClearLoads(projectData)),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public deleteLoad(load) {
    return this.http.post(this.url + 'project/load/delete', load).pipe(
      tap((projectData: Project) => this.handleSuccessWithClearLoads(projectData)),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public editLoad(load: any): Observable<any> {
    return this.http.post(this.url + 'project/load/edit', load).pipe(
      tap((projectData: Project) => this.handleSuccessWithClearLoads(projectData)),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public changeFloor(load) {
    return this.http.post(this.url + 'project/load/changeFloor', load).pipe(
      tap((projectData: Project) => this.handleSuccessWithClearLoads(projectData)),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public dragLoad(dragData) {
    return this.http
      .post(this.url + 'project/load/drag', dragData)
      .pipe(catchError((error: HttpErrorResponse) => this.handleError(error)));
  }

  public dragManyLoads(dragData) {
    return this.http
      .post(this.url + 'project/load/drag/many', dragData)
      .pipe(catchError((error: HttpErrorResponse) => this.handleError(error)));
  }

  public flipLoad(load: any) {
    return this.http.post(this.url + 'project/load/flip', load).pipe(
      tap((projectData: Project) => this.handleSuccessWithClearLoads(projectData)),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public rotateLoad(data: rotationData) {
    return this.http.post(this.url + 'project/load/rotateLoad', data).pipe(
      tap((projectData: Project) => this.handleSuccessWithClearLoads(projectData)),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public repack() {
    this.http
      .post(this.url + 'project/repack', {})
      .pipe(catchError((error: HttpErrorResponse) => this.handleError(error)))
      .subscribe((projectData: Project) => this.handleSuccessWithClearLoads(projectData));
  }

  public clear() {
    this.http
      .post(this.url + 'project/clear', {})
      .pipe(catchError((error: HttpErrorResponse) => this.handleError(error)))
      .subscribe((projectData: Project) => this.handleSuccessWithClearLoads(projectData));
  }

  public addLoadingSpace(loadingSpace): Observable<any> {
    return this.http.post(this.url + 'project/loadingspace/add', loadingSpace).pipe(
      tap((projectData: Project) => this.handleSuccessWithClearLoads(projectData)),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public updateLoadingSpace(loadingSpace): Observable<any> {
    const targetWorkSpaceId = loadingSpace?.target?.workSpaceId;
    const previousQuantity = palletSpaceQuantity(
      this.projectService.getState()?.workSpaces?.find((space) => space.id === targetWorkSpaceId),
    );
    return this.http.post(this.url + 'project/loadingspace/update', loadingSpace).pipe(
      switchMap((projectData: Project) => {
        const quantityDifference = palletQuantityDifferenceToRestoreOnVehicle(
          projectData,
          targetWorkSpaceId,
          previousQuantity,
        );
        const finish = (project: Project) => {
          restorePalletWorkspaceQuantity(project, targetWorkSpaceId, previousQuantity);
          this.handleSuccessWithClearLoads(project);
          return project;
        };
        if (!quantityDifference) {
          return of(finish(projectData));
        }
        return this.changePalletQuantity({
          currentWorkSpaceId: loadingSpace?.currentWorkSpaceId,
          target: { workSpaceId: targetWorkSpaceId },
          quantityDifference,
        }).pipe(map(finish));
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public deleteLoadingSpace(loadingSpace): Observable<any> {
    return this.http.post(this.url + 'project/loadingspace/delete', loadingSpace).pipe(
      tap((projectData: Project) => this.handleSuccessWithClearLoads(projectData)),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public changePalletQuantity(body: {
    currentWorkSpaceId?: number;
    target: { workSpaceId: number };
    quantityDifference: number;
  }): Observable<Project> {
    return this.http.post(this.url + 'project/loadingspace/changePalletQuantity', body).pipe(
      tap((projectData: Project) => this.handleSuccessWithClearLoads(projectData)),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public recommendLoadingSpace(body: { target: { workSpaceId: number } }): Observable<Project> {
    const viewedWorkSpace = this.projectService.getCurrentWorkSpace();
    return this.http.post(this.url + 'project/loadingspace/recommendation', body).pipe(
      tap((projectData: Project) => {
        this.handleSuccessWithClearLoads(projectData);
        this.keepViewedWorkSpace(projectData, viewedWorkSpace);
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  /** Stay on the palette the user was looking at after a recommendation adds spaces. */
  private keepViewedWorkSpace(project: Project, viewedWorkSpace: number): void {
    const count = project?.workSpaces?.length ?? 0;
    if (count === 0) {
      this.projectService.setCurrentWorkSpace(0);
      return;
    }
    if (viewedWorkSpace >= count) {
      this.projectService.setCurrentWorkSpace(count - 1);
    }
  }

  public acceptRecommendation(id: number): Observable<any> {
    this.projectService.setCurrentWorkSpace(0);
    return this.http
      .post(this.url + 'project/recommendation/accept', {
        recommendationId: id,
      })
      .pipe(
        tap((projectData: Project) => this.handleSuccessWithClearLoads(projectData)),
        catchError((error: HttpErrorResponse) => this.handleError(error)),
      );
  }

  public closeRecommendation(): Observable<any> {
    return this.http.post(this.url + 'project/recommendation/cancel', {}).pipe(
      tap((projectData: Project) => this.handleSuccess(projectData)),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public calculateRecommendations(): Observable<any> {
    return this.http
      .post(this.url + 'project/recommendation/calculate', {})
      .pipe(catchError((error: HttpErrorResponse) => this.handleError(error)));
  }

  public share(data: { name: string; note: string } = null): Observable<any> {
    return this.http.post(this.url + 'project/shared', data).pipe(
      map((shareData: ShareObject) => this.handleShareSuccess(shareData)),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public shareCompany(data: { name: string; note: string } = null): Observable<any> {
    return this.http.post(this.url + 'project/shareCompany', data).pipe(
      map((shareData: ShareObject) => this.handleShareSuccess(shareData)),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public getSharedProject(id: string, version: number): Observable<any> {
    const url = version
      ? `${this.url}project/shared/?id=${id}&version=${version}`
      : `${this.url}project/shared/?id=${id}`;
    return this.http.get(url).pipe(
      map((sharedProject: SharedProject) => {
        this.handleGetSharedProject(sharedProject, id);
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  private handleGetSharedProjectVersion(sharedProject): void {
    this.projectService.setState(sharedProject.project);
    this.sharedProjectService.setSharedProject(sharedProject);
  }

  public getSharedProjectVersion(id: string, version?: number): Observable<any> {
    const url = version
      ? `${this.url}project/shared/?id=${id}&version=${version}`
      : `${this.url}project/shared/?id=${id}`;
    return this.http.get(url).pipe(
      tap((sharedProject: SharedProject) => {
        this.handleGetSharedProjectVersion(sharedProject);
      }),
      tap((projectData: SharedProject) => this.handleSuccessWithClearLoads(projectData.project)),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public editSharedProject(shareId: string, version: number = null): Observable<any> {
    const msgBody =
      version !== null && version !== undefined
        ? { id: shareId, version: version }
        : {
            id: shareId,
          };
    return this.http.post(this.url + 'project/shared/edit', msgBody).pipe(
      tap((projectData: Project) => this.handleSuccessWithClearLoads(projectData)),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public checkCOGNotification(project: Project): void {
    const projectLength = project?.workSpaces.length;
    const isCOGNotification = this.notificationService.isNotifiaction(
      this.translate.instant('notification_center_of_gravity_exceeded'),
    );
    if (projectLength === 0 && isCOGNotification) {
      this.notificationService.removeNotification(
        this.translate.instant('notification_center_of_gravity_exceeded'),
      );
    } else if (
      projectLength > 0 &&
      isCOGNotification &&
      project.workSpaces[this.currentWorkSpace].cargoSpaces[0].extraFeatures.COGflag !== false
    ) {
      this.notificationService.removeNotification(
        this.translate.instant('notification_center_of_gravity_exceeded'),
      );
    } else if (
      projectLength > 0 &&
      !isCOGNotification &&
      project.workSpaces[this.currentWorkSpace].cargoSpaces[0].extraFeatures.COGflag === false
    ) {
      this.notificationService.generateCOGNotification(project);
    }
  }

  public checkLoadsNotDisplayedNotification(project: Project): void {
    const projectLength = project?.workSpaces.length;
    const isLoadsNotDisplayedNotification = this.notificationService.isNotifiaction(
      this.translate.instant('notification_loads_not_displayed'),
    );
    if (projectLength > 0 && isLoadsNotDisplayedNotification) {
      this.notificationService.removeNotification(
        this.translate.instant('notification_loads_not_displayed'),
      );
    }
  }

  public handleSuccess(project: Project): Project {
    this.normalizeProjectPalletLayerGroups(project);
    this.checkCOGNotification(project);
    this.checkLoadsNotDisplayedNotification(project);
    this.projectService.setState(project);
    this.projectHistory.updateHistory();
    this.palletOverflowRecommendation.offerIfNeeded(project);
    return project;
  }

  private extractProjectFromResponse(projectData: Project | { project: Project }): Project {
    return (projectData as { project?: Project })?.project
      ? (projectData as { project: Project }).project
      : (projectData as Project);
  }

  private normalizeProjectPalletLayerGroups(project: Project): void {
    project?.workSpaces?.forEach((workSpace) => {
      workSpace.cargoSpaces?.forEach((cargoSpace) => normalizeCargoSpacePalletLayerGroups(cargoSpace));
      workSpace.stops?.forEach((stop) =>
        stop.cargoSpaces?.forEach((cargoSpace) => normalizeCargoSpacePalletLayerGroups(cargoSpace)),
      );
    });
  }

  private handleShareSuccess(shareData: ShareObject): string {
    return shareData.id;
  }

  private handleReplaceSuccess(project: Project) {
    LoadFactory.clear();
    this.projectService.setState(project);
  }

  private handleAddLoadSuccess(project: Project) {
    this.palletOverflowRecommendation.markLoadAdded();
    this.handleSuccessWithClearLoads(project);
  }

  private handleSuccessWithClearLoads(project: Project) {
    LoadFactory.clear();
    return this.handleSuccess(project);
  }

  private handleSuccessProjectInfo(projectInfo: ProjectInfo): ProjectInfo {
    this.currentProjectName = projectInfo !== null ? projectInfo.name : '';
    this.currentProjectNote = projectInfo !== null ? projectInfo.note : '';
    this.currentProjectId = projectInfo !== null ? `${projectInfo.loadedProjectId}` : '';
    return projectInfo;
  }

  private handleSetUnitSuccess(project: Project) {
    LoadFactory.clear();
    this.projectService.setState(project);
    this.projectHistory.clearHistory();
    this.userApiService.getUser().subscribe();
  }

  private handleGetSharedProject(sharedProject: SharedProject, id: string): void {
    this.currentProjectName = sharedProject.name;
    this.sharedProjectService.startSharedView();
    this.sharedProjectService.setProjectOwner(sharedProject.email);
    this.sharedProjectService.setSharedProjectId(id);
    this.projectService.setState(sharedProject.project);
    this.sharedProjectService.setSharedProject(sharedProject);
  }

  private handleError(error: HttpErrorResponse) {
    if (error.status === 401) {
      this.router.navigate([this.translate.currentLang, 'auth'], {
        relativeTo: this.route,
      });
    } else if (error.status === 403) {
      window.location.reload();
    }
    return throwError(error);
  }

  private handleErrorWithoutValid() {
    return throwError('Error happend');
  }
}

export interface AddGroup {
  quantity: number;
  load: Partial<Load>;
}

interface ShareObject {
  id: string;
}
